import { useEffect, useMemo, useState } from "react";
import { Plus, Loader2, Pencil, Trash2, CalendarClock } from "lucide-react";
import { api, inr } from "../../lib/api";
import { Field, Modal } from "./AdminRestaurants";
import { toast } from "sonner";
import AdminSearchBar from "../../components/AdminSearchBar";
import AdminShopPicker from "../../components/AdminShopPicker";
import MultiImageUploader from "../../components/MultiImageUploader";
import ProductVariantsEditor from "../../components/ProductVariantsEditor";
import { isFoodShop } from "../../lib/foodCategory";
import { useLse } from "../../lib/lse";

const EMPTY = {
    restaurant_id: "", name: "", food_type: "VEG", description: "", images: [],
    category_ids: [], menu_price: "",
    sp_increment_type: "", sp_increment_value: "",
    mrp_increment_type: "", mrp_increment_value: "",
    commission_type: "", commission_value: "",
    gst_percent: "",
    variants: [],
    // Service-only fields (mirror MerchantMenu so tenant admins can also add services)
    kind: "PRODUCT",
    duration_min: 60,
    slot_minutes: 30,
    advance_days: 7,
    weekday_open: "09:00",
    weekday_close: "20:00",
    advance_payment_pct: 20,
};

export default function AdminProducts() {
    // Location Service Engine: brand + SKU fields (only when the tenant has product_brand_sku on).
    const brandSku = useLse().has("product_brand_sku");
    const [rows, setRows] = useState([]);
    const [rests, setRests] = useState([]);
    const [cats, setCats] = useState([]);
    const [verticals, setVerticals] = useState([]);
    const [filter, setFilter] = useState("");
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [query, setQuery] = useState("");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((p) => {
            const hay = `${p.name || ""} ${p.category_name || ""} ${p.food_type || ""} ${p.status || ""} ${p.description || ""}`.toLowerCase();
            return hay.includes(q);
        });
    }, [rows, query]);

    const load = () => {
        const url = filter ? `/admin/products?restaurant_id=${filter}` : "/admin/products";
        api.get(url).then(({ data }) => setRows(data));
    };
    useEffect(() => { api.get("/admin/restaurants").then(({ data }) => setRests(data)); }, []);
    useEffect(() => {
        api.get("/admin/business-categories")
            .then(({ data }) => setVerticals(data || []))
            .catch(() => setVerticals([]));
    }, []);
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    // Load categories for the selected shop (used in the edit/create modal).
    // Wrapping the empty-shop path through ``Promise.resolve`` defers
    // the ``setCats([])`` call out of the synchronous useEffect body —
    // the React lint rule ``set-state-in-effect`` flags synchronous
    // setState calls inside effects because they can cause infinite
    // render loops. We've manually verified the dependency array gates
    // this; the defer is purely to silence the (correct) lint warning.
    useEffect(() => {
        if (!form.restaurant_id) {
            Promise.resolve().then(() => setCats([]));
            return;
        }
        api.get(`/admin/product-categories?restaurant_id=${form.restaurant_id}`)
            .then(({ data }) => setCats(data));
    }, [form.restaurant_id]);

    const selectedShop = useMemo(
        () => rests.find((r) => r.id === form.restaurant_id),
        [rests, form.restaurant_id],
    );
    const showFoodType = isFoodShop(selectedShop, verticals);

    const parentCats = useMemo(() => cats.filter((c) => !c.parent_id), [cats]);
    const subsBy = useMemo(() => {
        const m = {};
        for (const c of cats) if (c.parent_id) (m[c.parent_id] = m[c.parent_id] || []).push(c);
        return m;
    }, [cats]);

    const openCreate = (kind = "PRODUCT") => { setEditId(null); setForm({ ...EMPTY, kind }); setOpen(true); };
    const openEdit = (p) => {
        setEditId(p.id);
        const cfg = p.service_config || {};
        setForm({
            restaurant_id: p.restaurant_id,
            name: p.name || "", food_type: p.food_type || "VEG",
            description: p.description || "",
            image_url: undefined,
            // iter121 — full image list (fallback to legacy single-image
            // for products that pre-date multi-image support).
            images: Array.isArray(p.images) ? p.images.filter(Boolean) : [],
            category_ids: p.category_ids || (p.category_id ? [p.category_id] : []),
            menu_price: p.menu_price ?? p.selling_price ?? "",
            sp_increment_type: p.sp_increment_type || "",
            sp_increment_value: p.sp_increment_value ?? "",
            mrp_increment_type: p.mrp_increment_type || "",
            mrp_increment_value: p.mrp_increment_value ?? "",
            commission_type: p.commission_type || "",
            commission_value: p.commission_value ?? "",
            gst_percent: p.gst_percent ?? "",
            variants: Array.isArray(p.variants) ? p.variants : [],
            kind: p.kind || "PRODUCT",
            duration_min: cfg.duration_min ?? 60,
            slot_minutes: cfg.slot_minutes ?? 30,
            advance_days: cfg.advance_days ?? 7,
            weekday_open: cfg.weekday_open ?? "09:00",
            weekday_close: cfg.weekday_close ?? "20:00",
            advance_payment_pct: cfg.advance_payment_pct ?? 20,
            brand: p.brand || "", sku: p.sku || "",
        });
        setOpen(true);
    };

    const submit = async () => {
        if (!form.name || !form.menu_price || !form.restaurant_id) { toast.error("Shop, name & menu price are required"); return; }
        setBusy(true);
        const isService = form.kind === "SERVICE";
        const payload = {
            name: form.name, description: form.description,
            // Veg/Non-Veg only applies to food shops; force a neutral value otherwise.
            food_type: showFoodType ? form.food_type : "VEG",
            images: Array.isArray(form.images) ? form.images.filter(Boolean) : [],
            category_ids: form.category_ids,
            category_id: form.category_ids[0] || null,
            category_name: cats.find((c) => c.id === form.category_ids[0])?.name || null,
            menu_price: Number(form.menu_price),
            sp_increment_type: form.sp_increment_type || null,
            sp_increment_value: form.sp_increment_value === "" ? null : Number(form.sp_increment_value),
            mrp_increment_type: form.mrp_increment_type || null,
            mrp_increment_value: form.mrp_increment_value === "" ? null : Number(form.mrp_increment_value),
            commission_type: form.commission_type || null,
            commission_value: form.commission_value === "" ? null : Number(form.commission_value),
            gst_percent: form.gst_percent === "" ? null : Number(form.gst_percent),
            variants: (form.variants || []).filter((v) => (v.label || "").trim()).map((v, idx) => ({
                ...v,
                sort_order: idx,
                selling_price: Number(v.selling_price) || 0,
                mrp: Number(v.mrp) || 0,
            })),
            kind: form.kind,
            service_config: isService ? {
                duration_min: Number(form.duration_min) || 60,
                slot_minutes: Number(form.slot_minutes) || 30,
                advance_days: Number(form.advance_days) || 7,
                weekday_open: form.weekday_open || "09:00",
                weekday_close: form.weekday_close || "20:00",
                advance_payment_pct: Number(form.advance_payment_pct) || 0,
            } : null,
            ...(brandSku ? { brand: (form.brand || "").trim() || null, sku: (form.sku || "").trim() || null } : {}),
        };
        try {
            if (editId) await api.patch(`/admin/products/${editId}`, payload);
            else await api.post(`/admin/products`, { restaurant_id: form.restaurant_id, ...payload });
            toast.success(editId ? "Updated" : (isService ? "Service added" : "Added")); setOpen(false); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!confirm("Delete this product? Past orders will keep their reference.")) return;
        try {
            const { data } = await api.delete(`/admin/products/${id}`);
            toast.success(data?.mode === "archived"
                ? `Archived — ${data.orders} past order(s) kept`
                : "Deleted");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
    };

    const toggleCat = (cid, parent_id) => setForm((f) => {
        const cur = new Set(f.category_ids || []);
        if (cur.has(cid)) {
            cur.delete(cid);
        } else {
            cur.add(cid);
            // When selecting a subcategory, auto-include its parent so customer
            // filters that look only at the top level still match.
            if (parent_id) cur.add(parent_id);
        }
        return { ...f, category_ids: [...cur] };
    });

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Products</h1>
                    <p className="text-sm text-muted-foreground mt-1">{rows.length} items{query ? ` · ${filtered.length} match` : ""}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <AdminSearchBar value={query} onChange={setQuery} placeholder="Search products…" testid="products-search" />
                    <AdminShopPicker
                        testIdPrefix="filter-restaurant"
                        value={filter}
                        onChange={setFilter}
                        allShopsOption
                        shopsOverride={rests}
                    />
                    <button data-testid="add-product" onClick={() => openCreate("PRODUCT")} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2"><Plus size={16} /> Add Product</button>
                    <button data-testid="add-service" onClick={() => openCreate("SERVICE")} className="bg-violet-600 text-white rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2"><CalendarClock size={16} /> Add Service</button>
                </div>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.map((p) => (
                    <div key={p.id} data-testid={`prod-card-${p.id}`} className="bg-card border border-border rounded-2xl p-4 flex gap-3">
                        <div className="w-20 h-20 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                            {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                                <p className="font-bold text-sm line-clamp-1">{p.name}</p>
                                {p.kind === "SERVICE" && (
                                    <span className="text-[9px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5"><CalendarClock size={9} /> Service</span>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground">{p.category_name || "—"}{p.food_type && p.food_type !== "VEG" ? ` • ${p.food_type}` : ""}</p>
                            <p className="text-xs mt-1">Menu {inr(p.menu_price ?? p.selling_price)} → Sell <strong>{inr(p.selling_price)}</strong></p>
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${p.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>{p.status}</span>
                        </div>
                        <div className="flex flex-col gap-1">
                            <button onClick={() => openEdit(p)} data-testid={`edit-prod-${p.id}`} className="p-2 rounded-lg hover:bg-muted"><Pencil size={14} /></button>
                            <button onClick={() => remove(p.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
                {filtered.length === 0 && (
                    <p className="col-span-full text-center text-muted-foreground text-sm py-10">{query ? `No products match "${query}"` : "No products yet"}</p>
                )}
            </div>

            {open && (
                <Modal title={editId ? `Edit ${form.kind === "SERVICE" ? "service" : "product"}` : (form.kind === "SERVICE" ? "Add Service" : "Add Product")} onClose={() => setOpen(false)}>
                    <div className="grid grid-cols-2 gap-3">
                        <Field full label="Shop">
                            <select data-testid="form-prod-rest" className="inp" value={form.restaurant_id} disabled={!!editId}
                                onChange={(e) => setForm({ ...form, restaurant_id: e.target.value, category_ids: [] })}>
                                <option value="">Select…</option>
                                {rests.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </select>
                        </Field>
                        <Field full label="Name"><input data-testid="form-prod-name" className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                        {brandSku && (
                            <>
                                <Field label="Brand"><input data-testid="form-prod-brand" className="inp" maxLength={80} value={form.brand || ""} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></Field>
                                <Field label="SKU"><input data-testid="form-prod-sku" className="inp" maxLength={64} value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></Field>
                            </>
                        )}
                        {showFoodType && form.kind !== "SERVICE" && (
                            <Field label="Type">
                                <select data-testid="form-prod-food-type" className="inp" value={form.food_type} onChange={(e) => setForm({ ...form, food_type: e.target.value })}>
                                    <option>VEG</option><option>NON_VEG</option><option>EGG</option>
                                </select>
                            </Field>
                        )}
                        <Field label={form.kind === "SERVICE" ? "Service Price ₹" : "Menu Price ₹"}><input data-testid="form-menu-price" type="number" className="inp" value={form.menu_price} onChange={(e) => setForm({ ...form, menu_price: e.target.value })} /></Field>
                        <Field label="GST % (optional)">
                            <input
                                data-testid="form-gst-percent"
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                className="inp"
                                value={form.gst_percent}
                                onChange={(e) => setForm({ ...form, gst_percent: e.target.value })}
                                placeholder="Inherits from category / shop"
                            />
                            <span className="text-[11px] text-muted-foreground mt-1 block">
                                Leave blank to inherit. <code>0</code> = tax-free.
                            </span>
                        </Field>
                        <Field full label="Categories (pick one or more)">
                            <div className="space-y-2">
                                {parentCats.map((p) => {
                                    const subs = subsBy[p.id] || [];
                                    const onP = form.category_ids.includes(p.id);
                                    return (
                                        <div key={p.id}>
                                            <button type="button" onClick={() => toggleCat(p.id, null)} data-testid={`cat-pick-${p.id}`}
                                                className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${onP ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}>
                                                {p.name}
                                            </button>
                                            {subs.length > 0 && (
                                                <div className="flex flex-wrap gap-1.5 mt-1.5 ml-4">
                                                    {subs.map((s) => {
                                                        const on = form.category_ids.includes(s.id);
                                                        return (
                                                            <button type="button" key={s.id} onClick={() => toggleCat(s.id, p.id)} data-testid={`sub-pick-${s.id}`}
                                                                className={`text-[11px] rounded-full px-2.5 py-1 border ${on ? "bg-primary/15 text-primary border-primary/40" : "bg-muted border-border text-muted-foreground"}`}>
                                                                {s.name}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {parentCats.length === 0 && <span className="text-xs text-muted-foreground">Pick a shop first, then add categories from the “Categories” page.</span>}
                            </div>
                        </Field>
                        <Field full label="Product images">
                            <MultiImageUploader
                                value={form.images}
                                onChange={(imgs) => setForm({ ...form, images: imgs })}
                                testId="product-images"
                            />
                        </Field>
                        <Field full label="Description"><textarea rows={2} className="inp" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
                    </div>

                    {form.kind !== "SERVICE" && (
                        <div className="mt-4 border-t border-border pt-4">
                            <ProductVariantsEditor
                                value={form.variants}
                                onChange={(vs) => setForm({ ...form, variants: vs })}
                                testId="adminprod-variants"
                            />
                        </div>
                    )}

                    {form.kind === "SERVICE" && (
                        <div className="mt-4 border-t border-border pt-4">
                            <h3 className="text-sm font-bold inline-flex items-center gap-2 mb-3"><CalendarClock size={14} className="text-violet-700" /> Booking settings</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Duration (min)"><input data-testid="svc-duration" type="number" className="inp" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} /></Field>
                                <Field label="Slot every (min)"><input data-testid="svc-slot" type="number" className="inp" value={form.slot_minutes} onChange={(e) => setForm({ ...form, slot_minutes: e.target.value })} /></Field>
                                <Field label="Open at"><input data-testid="svc-open" type="time" className="inp" value={form.weekday_open} onChange={(e) => setForm({ ...form, weekday_open: e.target.value })} /></Field>
                                <Field label="Close at"><input data-testid="svc-close" type="time" className="inp" value={form.weekday_close} onChange={(e) => setForm({ ...form, weekday_close: e.target.value })} /></Field>
                                <Field label="Book up to (days ahead)"><input data-testid="svc-advance-days" type="number" className="inp" value={form.advance_days} onChange={(e) => setForm({ ...form, advance_days: e.target.value })} /></Field>
                                <Field label="Advance payment (%)"><input data-testid="svc-advance-pct" type="number" className="inp" value={form.advance_payment_pct} onChange={(e) => setForm({ ...form, advance_payment_pct: e.target.value })} /></Field>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-2">Customer pays {form.advance_payment_pct || 0}% advance ({form.menu_price ? `₹${Math.round((Number(form.menu_price) * (Number(form.advance_payment_pct) || 0)) / 100)}` : "—"}) via mock Razorpay. Remainder due at service.</p>
                        </div>
                    )}

                    <details className="mt-4 bg-muted/40 rounded-xl p-3">
                        <summary className="text-sm font-bold cursor-pointer">Per-product pricing overrides</summary>
                        <p className="text-xs text-muted-foreground mt-2">Blank = inherit from this shop&apos;s Pricing settings.</p>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <OverrideRow label="SP Increment" t={form.sp_increment_type} v={form.sp_increment_value} onT={(x) => setForm({ ...form, sp_increment_type: x })} onV={(x) => setForm({ ...form, sp_increment_value: x })} />
                            <OverrideRow label="MRP Increment" t={form.mrp_increment_type} v={form.mrp_increment_value} onT={(x) => setForm({ ...form, mrp_increment_type: x })} onV={(x) => setForm({ ...form, mrp_increment_value: x })} />
                            <OverrideRow label="Commission" t={form.commission_type} v={form.commission_value} onT={(x) => setForm({ ...form, commission_type: x })} onV={(x) => setForm({ ...form, commission_value: x })} />
                        </div>
                    </details>

                    <button data-testid="save-product" disabled={busy || !form.name || !form.restaurant_id || !form.menu_price} onClick={submit}
                        className={`mt-4 w-full ${form.kind === "SERVICE" ? "bg-violet-600 text-white" : "bg-primary text-primary-foreground"} rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50`}>
                        {busy && <Loader2 size={16} className="animate-spin" />} Save {form.kind === "SERVICE" ? "service" : "product"}
                    </button>
                </Modal>
            )}
        </div>
    );
}

function OverrideRow({ label, t, v, onT, onV }) {
    return (
        <Field label={label}>
            <div className="flex gap-2">
                <select className="inp" value={t} onChange={(e) => onT(e.target.value)}>
                    <option value="">Inherit</option>
                    <option value="FLAT">Flat ₹</option>
                    <option value="PERCENT">Percent %</option>
                </select>
                <input type="number" className="inp" value={v} onChange={(e) => onV(e.target.value)} placeholder="0" disabled={!t} />
            </div>
        </Field>
    );
}
